// src/components/home/MediaLightbox.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const MediaLightbox = ({ media = [], initialIndex = 0, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const handlersRef = useRef({ goPrev: null, goNext: null });

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + media.length) % media.length);
  }, [media.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % media.length);
  }, [media.length]);

  handlersRef.current = { goPrev, goNext };

  // Keyboard controls (stable listener)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') handlersRef.current.goPrev?.();
      if (e.key === 'ArrowRight') handlersRef.current.goNext?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Body scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Clamp index if media changes
  useEffect(() => {
    if (index >= media.length) setIndex(0);
  }, [media.length, index]);

  if (!media.length) return null;
  const current = media[index];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Media viewer"
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center z-30 transition"
        >
          <i className="fas fa-times text-lg" aria-hidden="true" />
        </button>

        {/* Counter */}
        {media.length > 1 && (
          <div className="absolute top-4 left-4 bg-black/60 text-white text-sm rounded-full px-3 py-1 z-30">
            {index + 1} / {media.length}
          </div>
        )}

        {/* Cover badge */}
        {current?.isCover && media.length > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-orange text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-lg z-30">
            <i className="fas fa-star text-[10px]" aria-hidden="true" />
            Cover
          </div>
        )}

        {/* Prev */}
        {media.length > 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-30 transition"
          >
            <i className="fas fa-chevron-left text-xl" aria-hidden="true" />
          </button>
        )}

        {/* Next */}
        {media.length > 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-30 transition"
          >
            <i className="fas fa-chevron-right text-xl" aria-hidden="true" />
          </button>
        )}

        {/* Media */}
        <div
          className="max-w-full max-h-full w-full h-full flex items-center justify-center p-4"
          onClick={(e) => e.stopPropagation()}
        >
          {current.type === 'video' ? (
            <video
              key={current.url}
              src={current.url}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
          ) : (
            <img
              key={current.url}
              src={current.url}
              alt=""
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
              draggable={false}
            />
          )}
        </div>

        {/* Dots */}
        {media.length > 1 && media.length <= 12 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-30">
            {media.map((m, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
                aria-label={`Go to item ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === index ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'
                } ${m.type === 'video' ? 'ring-1 ring-white/70' : ''}`}
              />
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default MediaLightbox;